/**
 * The proposer's title (salutation), as Reliance's own Title list spells it: Mr. / Ms. / Mrs.
 *
 * Reliance's list also has M/S, but that is the company (Corporate) title: Reliance refuses it for an individual customer
 * ("Please select Salutation. Please select Gender."), and every policy here is an individual. So the form does not offer
 * it (removed 2026-10-16, the owner's call); a policy saved earlier with M/S is sent with its Gender's title instead
 * (titleToSend), and its edit form asks for one of the three.
 *
 * Copy of RayalBrokers-backend/shared/proposerTitle.js (the repos keep their own copies, like policyDates.js); a test
 * in each pins the list. The operator picks one on the Online Policy form; it starts from the Gender and follows it
 * until the operator has chosen (see CustomerDetailsStep).
 */
export const PROPOSER_TITLES = ["Mr.", "Ms.", "Mrs."];

const KEY = (text) => String(text == null ? "" : text).toLowerCase().replace(/[\s.]+/g, "");
const BY_KEY = new Map(PROPOSER_TITLES.map((title) => [KEY(title), title]));

/** "mr", "MR.", " Mrs " -> the canonical option; anything else (including "" and "M/S") -> undefined. */
export const normalizeProposerTitle = (value) => BY_KEY.get(KEY(value));

/** The title a gender implies: male -> Mr., female -> Ms., otherwise none. */
export const titleFromGender = (gender) => {
  const g = String(gender == null ? "" : gender).trim().toLowerCase();
  if (g === "male") return "Mr.";
  if (g === "female") return "Ms.";
  return undefined;
};

/**
 * A salutation PRINTED on the card (some Aadhaar names read "SHRI RAMESH KUMAR" / "SMT. LATHA") -> Reliance's option.
 * Anything else, including a word that merely starts like one, is undefined.
 */
const SALUTATIONS = {
  mr: "Mr.", shri: "Mr.", shree: "Mr.", sh: "Mr.", mister: "Mr.",
  mrs: "Mrs.", smt: "Mrs.", smti: "Mrs.", srimathi: "Mrs.", shrimati: "Mrs.", missus: "Mrs.",
  ms: "Ms.", miss: "Ms.", kumari: "Ms.", km: "Ms.", selvi: "Ms.",
};
// Only these may be CUT OFF a printed name. "Sri", "Selvi" and "Kumari" are also ordinary given names ("SRI LAKSHMI",
// "SELVI", "KUMARI DEVI"): stripping them would corrupt the name. The model is asked for a salutation only when one
// is printed apart from the name; this list is the safety net for one that came back glued to the name.
const STRIPPABLE = new Set(["mr", "mrs", "ms", "miss", "shri", "smt", "shrimati"]);
export const titleFromSalutation = (text) => {
  const key = String(text == null ? "" : text).trim().toLowerCase().replace(/[.\s]+/g, "");
  return Object.prototype.hasOwnProperty.call(SALUTATIONS, key) ? SALUTATIONS[key] : undefined;
};

/** A leading salutation word on a name: { salutation, name } with the word split off ("SHRI RAMESH" -> "SHRI", "RAMESH"). */
export const splitLeadingSalutation = (name) => {
  const text = String(name == null ? "" : name).trim();
  const m = text.match(/^([A-Za-z/]+)\.?\s+(\S.*)$/);
  if (m && STRIPPABLE.has(m[1].toLowerCase())) return { salutation: m[1], name: m[2].trim() };
  return { salutation: null, name: text };
};

/** "S/O" | "D/O" | "W/O" | "C/O" from whatever the card or the model gave ("s/o Ramesh", "W.O", "d / o"), else "". */
export const relationFromText = (text) => {
  const m = String(text == null ? "" : text).match(/\b([SDWC])\s*[/.]\s*O\b/i);
  return m ? `${m[1].toUpperCase()}/O` : "";
};

/**
 * The title to suggest for a customer, from what the Aadhaar says.
 *
 *   1. a salutation printed on the card (Shri / Smt / Kumari ...)        — the card's own word
 *   2. the guardian prefix on the back: W/O (wife of) -> Mrs., D/O (daughter of) -> Ms., S/O (son of) -> Mr.
 *      C/O (care of) says nothing about the person and is ignored. A prefix that contradicts the gender
 *      (S/O on a female card) is ignored too — a misread is worse than the gender's plain answer.
 *   3. the gender: male -> Mr., female -> Ms. (the card never says whether a woman is married)
 *
 * No guess beyond that: "other" with nothing else returns undefined.
 */
export const suggestTitle = ({ salutation, relation, gender } = {}) => {
  const printed = titleFromSalutation(salutation);
  if (printed) return printed;

  const g = String(gender == null ? "" : gender).trim().toLowerCase();
  const rel = relationFromText(relation);
  const fromRelation = { "W/O": "Mrs.", "D/O": "Ms.", "S/O": "Mr." }[rel];
  const relationFits = !g || (rel === "S/O" ? g === "male" : g === "female");
  if (fromRelation && relationFits) return fromRelation;

  return titleFromGender(g);
};

/**
 * The title the Gender field (or an Aadhaar read) should set: the suggested title while the operator has not chosen
 * one; otherwise the title stays exactly as chosen.
 */
export const titleAfterGenderChange = ({ gender, currentTitle, titleChosen, salutation, relation }) => {
  if (titleChosen) return currentTitle || "";
  return suggestTitle({ salutation, relation, gender }) || currentTitle || "";
};

/** The title that is SENT: the chosen one, else what the gender implies, else Mr. (what was always sent). */
export const titleToSend = ({ proposerTitle, gender }) =>
  normalizeProposerTitle(proposerTitle) || titleFromGender(gender) || "Mr.";
